// "Search over Bluetooth / Operate over Bluetooth" — shown ONLY on the
// allow-listed bench device (core/ble/bleForced).
//
// Two buttons, because they are two different questions:
//
//   Search    can the phone SEE this device, and will it authenticate?
//             Answers on its own, without changing how anything is controlled.
//   Operate   pin this device's commands to BLE instead of MQTT, so the normal
//             relay buttons on the screen exercise the Bluetooth path.
//
// Splitting them matters during bring-up: if Operate does nothing, Search says
// whether the problem is the radio, the handshake, or the command.
//
// ⚠️ THE PIN IS NOT PERSISTED. It lasts as long as the app is open — a test
// switch left pinned to BLE after a reload would look broken to whoever picked
// the phone up next.

import React, { useEffect, useState } from 'react';
import {
  Box, Button, Typography, CircularProgress, Chip, useTheme,
} from '@mui/material';
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import BluetoothConnectedIcon from '@mui/icons-material/BluetoothConnected';
import { bleAutoSupported } from '../../../core/ble/bleClient';
import { ensureControlLink, closeControlLink, connectedUniqueNumber } from '../../../core/ble/bleControl';
import {
  canForceBle, isForcedBle, setForcedBle, onForcedBleChange,
} from '../../../core/ble/bleForced';

export default function BleOperateBar({ uniqueNumber }) {
  const theme = useTheme();
  const c = theme.sli;
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [, tick] = useState(0);

  useEffect(() => onForcedBleChange(() => tick((n) => n + 1)), []);

  // Leaving the screen must not leave a device pinned to a transport the next
  // screen knows nothing about.
  useEffect(() => () => {
    setForcedBle(uniqueNumber, false);
    closeControlLink();
  }, [uniqueNumber]);

  if (!canForceBle(uniqueNumber)) return null;

  // bleAutoSupported, not bleSupported: both buttons here go through
  // ensureControlLink, which finds the device by scanning for its name. The web
  // has no such scan — only a chooser — so that path is native-only, and
  // offering the buttons on the web would give two controls that always fail.
  // The BLE Test screen is where a browser can drive Bluetooth.
  if (!bleAutoSupported()) {
    return (
      <Typography sx={{ color: `${c.text}99`, fontSize: 12, textAlign: 'center', py: 0.5 }}>
        Bluetooth control needs the installed app.
      </Typography>
    );
  }

  const linked = connectedUniqueNumber() === String(uniqueNumber);
  const forced = isForcedBle(uniqueNumber);

  const search = async () => {
    setBusy(true);
    setStatus('Searching…');
    const session = await ensureControlLink(uniqueNumber);
    setBusy(false);
    setStatus(session
      ? 'Found and authenticated.'
      : 'Not found, or it refused the key. Check it is powered and in range.');
    tick((n) => n + 1);
  };

  const toggleOperate = async () => {
    if (forced) {
      setForcedBle(uniqueNumber, false);
      setStatus('Back on the cloud.');
      return;
    }
    setBusy(true);
    setStatus('Connecting…');
    const session = await ensureControlLink(uniqueNumber);
    setBusy(false);
    if (!session) {
      setStatus('Could not connect — commands stay on the cloud.');
      return;
    }
    setForcedBle(uniqueNumber, true);
    setStatus('Buttons on this screen now go over Bluetooth.');
  };

  const btn = {
    textTransform: 'none', borderRadius: '12px', fontSize: 13,
    color: c.text, borderColor: `${c.text}55`,
  };

  return (
    <Box sx={{ px: 2, pb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button
          size="small" variant="outlined" sx={btn} disabled={busy}
          startIcon={busy ? <CircularProgress size={14} sx={{ color: c.text }} /> : <BluetoothSearchingIcon />}
          onClick={search}
        >
          Search
        </Button>
        <Button
          size="small"
          variant={forced ? 'contained' : 'outlined'}
          sx={forced ? { ...btn, bgcolor: c.button, color: '#fff' } : btn}
          disabled={busy}
          startIcon={<BluetoothConnectedIcon />}
          onClick={toggleOperate}
        >
          {forced ? 'Operating over Bluetooth' : 'Operate over Bluetooth'}
        </Button>
        {linked && (
          <Chip size="small" label="linked" color="success" sx={{ height: 18, fontSize: 10 }} />
        )}
      </Box>
      {status && (
        <Typography sx={{ color: `${c.text}99`, fontSize: 11, textAlign: 'center' }}>
          {status}
        </Typography>
      )}
    </Box>
  );
}
